<template>
  <div class="training-file-upload" :class="`training-file-upload--${variant}`">
    <el-upload
      ref="upload"
      action=""
      :drag="variant === 'drag'"
      :auto-upload="false"
      :show-file-list="false"
      :multiple="multiple"
      :accept="acceptText"
      :disabled="disabled || uploading"
      :on-change="onFileChange">
      <template v-if="variant === 'drag'">
        <i class="el-icon-upload training-file-upload__icon"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
      </template>
      <el-button v-else :size="buttonSize" :type="buttonType" :icon="buttonIcon">
        {{ buttonText }}
      </el-button>
      <div slot="tip" class="el-upload__tip">{{ uploadTip }}</div>
    </el-upload>

    <div v-if="value.length" class="training-file-upload__list">
      <div
        v-for="(file, index) in value"
        :key="file.uid || file.url || index"
        class="training-file-upload__item">
        <el-image
          v-if="file.type === uploadTypes.IMAGE"
          :src="file.url"
          :preview-src-list="imagePreviewList"
          fit="cover"
          class="training-file-upload__thumb" />
        <i v-else :class="getFileIcon(file)" class="training-file-upload__file-icon"></i>
        <span class="training-file-upload__name" :title="file.name">{{ file.name }}</span>
        <el-button type="text" @click="openFile(file)">查看</el-button>
        <el-button type="text" :disabled="disabled" @click="removeFile(index)">删除</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatTrainingUploadFile,
  getFileExtension,
  getTrainingUploadConfig,
  TRAINING_UPLOAD_TYPES,
  validateTrainingUploadFile,
} from './training-upload'

export default {
  name: 'TrainingFileUpload',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    // 默认多图片上传
    // 文件类型
    acceptTypes: {
      type: Array,
      default: () => [TRAINING_UPLOAD_TYPES.IMAGE],
    },
    // 多文件上传
    multiple: {
      type: Boolean,
      default: true,
    },
    // 是否支持拖拽 button不支持，drag支持
    variant: {
      type: String,
      default: 'button',
    },
    // 按钮文字
    buttonText: {
      type: String,
      default: '上传文件',
    },
    // 类型
    buttonType: {
      type: String,
      default: 'default',
    },
    // 大小
    buttonSize: {
      type: String,
      default: 'mini',
    },
    // 图标
    buttonIcon: {
      type: String,
      default: 'el-icon-upload',
    },
    signedUrlApiName: {
      type: String,
      default: 'getTrainingSignedOssUrl',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      uploading: false,
      uploadTypes: TRAINING_UPLOAD_TYPES,
    }
  },
  computed: {
    ...Vuex.mapState({
      loginUser: state => state.login.user || {},
    }),
    uploadConfig () {
      return getTrainingUploadConfig(this.acceptTypes, this.multiple)
    },
    // 上传格式限制
    acceptText () {
      return this.uploadConfig.accept.concat(this.uploadConfig.extensions.map(ext => `.${ext}`)).join(',')
    },
    // 上传文件提示
    uploadTip () {
      return this.uploadConfig.tip
    },

    imagePreviewList () {
      return this.value.filter(file => file.type === TRAINING_UPLOAD_TYPES.IMAGE).map(file => file.url)
    },
  },
  methods: {
    clearFiles () {
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles()
      }
    },
    async onFileChange (file) {
      const raw = file.raw
      if (!raw) {
        return
      }

      const validation = validateTrainingUploadFile(raw, this.value, this.acceptTypes, this.multiple)
      if (!validation.valid) {
        this.$message.error(validation.message)
        this.clearFiles()
        return
      }

      this.uploading = true
      this.$emit('uploading-change', true)
      try {
        // 开始上传文件
        const uploadedFile = await this.uploadFile(raw)
        const nextValue = validation.replaceAll ? [uploadedFile] : this.value.concat(uploadedFile)
        this.$emit('input', nextValue)
        this.$emit('change', nextValue)
        this.$message.success(validation.replaceAll ? '文件已替换' : '上传成功')
      } catch (error) {
        this.$message.error(error.message || '上传失败')
      } finally {
        this.uploading = false
        this.$emit('uploading-change', false)
        this.clearFiles()
      }
    },
    async uploadFile (file) {
      const { data } = await request.common(this.signedUrlApiName, {
        file_name: file.name,
        ext: getFileExtension(file.name),
        content_type: file.type || 'application/octet-stream',
      })
      const uploadRes = await fetch(data.signedUrl, {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': data.content_type }),
        body: file,
      })
      if (!uploadRes.ok) {
        throw new Error(`上传失败: ${uploadRes.status}`)
      }
      return formatTrainingUploadFile(file, data.url, this.loginUser)
    },
    removeFile (index) {
      const nextValue = this.value.slice()
      nextValue.splice(index, 1)
      this.$emit('input', nextValue)
      this.$emit('change', nextValue)
    },
    openFile (file) {
      if (file && file.url) {
        window.open(file.url, '_blank')
      }
    },
    // 以后可以往里面加
    getFileIcon (file) {
      if (file.type === TRAINING_UPLOAD_TYPES.VIDEO) {
        return 'el-icon-video-camera'
      }
      return 'el-icon-document'
    },
  },
}
</script>

<style scoped lang="scss">
.training-file-upload--drag {
  max-width: 760px;
}

.training-file-upload--drag >>> .el-upload,
.training-file-upload--drag >>> .el-upload-dragger {
  width: 100%;
}

.training-file-upload__icon {
  font-size: 40px;
  color: #409EFF;
}

.training-file-upload__list {
  margin-top: 8px;
}

.training-file-upload__item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
}

.training-file-upload__thumb {
  width: 54px;
  height: 36px;
  border-radius: 4px;
  background: #f5f7fa;
}

.training-file-upload__file-icon {
  font-size: 20px;
  color: #409EFF;
}

.training-file-upload__name {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/views/training/components/TrainingFileUpload.vue